<template>
  <div class="outer-box max-w-xl">
    <h4>Field-Update Rate Limit (location + number)</h4>

    <b-alert show variant="info" class="text-left m-3">
      Inspects and (optionally) resets the per-(location, number, field) rate-limit counter
      that throttles updates to <code>forwardingNumber</code>, <code>assignedToNumber</code>,
      and <code>assignedUsers</code> on a Twilio number. Reset clears both the daily and
      weekly windows for every group containing the selected field.
    </b-alert>

    <div class="inner-box">
      <div class="form-group">
        <label class="form-label" for="rl-location">Location ID</label>
        <input
          id="rl-location"
          v-model="locationId"
          class="hl_input--wrap"
          type="text"
          placeholder="Enter Location ID"
        />
      </div>

      <div class="form-group">
        <label class="form-label" for="rl-number">Number (E.164)</label>
        <input
          id="rl-number"
          v-model="number"
          class="hl_input--wrap"
          type="text"
          placeholder="+14042345152"
        />
      </div>

      <div class="form-group">
        <label class="form-label" for="rl-field">Field</label>
        <select id="rl-field" v-model="field" class="hl_input--wrap">
          <option v-for="opt in fieldOptions" :key="opt" :value="opt">{{ opt }}</option>
        </select>
      </div>

      <div class="d-flex">
        <button
          class="btn btn-primary mr-2"
          :disabled="!canSubmit || isProcessing"
          @click="fetchRateLimit"
        >
          <moon-loader
            v-if="isProcessing && lastAction === 'read'"
            :loading="true"
            size="21px"
            color="#fafafa"
          />
          <span v-else>Read</span>
        </button>

        <button
          class="btn btn-warning"
          :disabled="!canSubmit || isProcessing"
          @click="showResetConfirmation"
        >
          <moon-loader
            v-if="isProcessing && lastAction === 'reset'"
            :loading="true"
            size="21px"
            color="#fafafa"
          />
          <span v-else>Reset</span>
        </button>
      </div>

      <pre
        v-if="response"
        style="background-color: #f2f7fa; padding: 16px; margin-top: 16px"
      >{{ response }}</pre>
    </div>

    <b-modal
      id="confirmRateLimitResetModal"
      ref="confirmModal"
      title="Confirm Rate-Limit Reset"
      ok-variant="warning"
      ok-title="Yes, reset counters"
      cancel-title="Cancel"
      @ok="performReset"
    >
      <div class="mb-3">
        <strong>Location:</strong> {{ locationId }}<br />
        <strong>Number:</strong> {{ number }}<br />
        <strong>Field:</strong> {{ field }}
      </div>
      <div>
        This will clear daily and weekly counters for the matching rate-limit group(s).
        Agency users will be able to update this field again immediately.
      </div>
    </b-modal>
  </div>
</template>

<script>
import { LCPhoneService } from "@/services";

const FIELD_OPTIONS = [
  "forwardingNumber",
  "assignedToNumber",
  "assignedUsers",
];

export default {
  name: "LocationNumberRateLimit",
  data() {
    return {
      locationId: "",
      number: "",
      field: FIELD_OPTIONS[0],
      fieldOptions: FIELD_OPTIONS,
      isProcessing: false,
      lastAction: null,
      response: null,
    };
  },
  computed: {
    canSubmit() {
      return (
        this.locationId.trim() !== "" &&
        this.number.trim() !== "" &&
        this.field !== ""
      );
    },
  },
  methods: {
    isValidLocationId(id) {
      return id && /^[a-zA-Z0-9]{10,50}$/.test(id.trim());
    },
    validateInputs() {
      if (!this.isValidLocationId(this.locationId)) {
        this.response = {
          error: "Invalid Location ID. Enter only the location ID, not a URL.",
        };
        return false;
      }
      if (!this.number.trim()) {
        this.response = { error: "Number is required (E.164 format)." };
        return false;
      }
      return true;
    },
    buildPayload() {
      return {
        locationId: this.locationId.trim(),
        number: this.number.trim(),
        field: this.field,
      };
    },
    async fetchRateLimit() {
      if (!this.validateInputs()) return;
      this.isProcessing = true;
      this.lastAction = "read";
      this.response = null;
      try {
        const res = await LCPhoneService.getFieldUpdateRateLimit(this.buildPayload());
        this.response = res.data;
      } catch (error) {
        this.response = error.response?.data || { error: error.message };
      } finally {
        this.isProcessing = false;
        this.lastAction = null;
      }
    },
    showResetConfirmation() {
      if (!this.validateInputs()) return;
      this.$refs.confirmModal.show();
    },
    async performReset() {
      this.isProcessing = true;
      this.lastAction = "reset";
      this.response = null;
      try {
        const res = await LCPhoneService.resetFieldUpdateRateLimit(this.buildPayload());
        this.response = res.data;
      } catch (error) {
        this.response = error.response?.data || { error: error.message };
      } finally {
        this.isProcessing = false;
        this.lastAction = null;
      }
    },
  },
};
</script>

<style scoped>
.inner-box {
  text-align: left;
  width: 500px;
}
.form-label {
  font-weight: 600;
  margin-bottom: 4px;
  color: #495057;
  display: block;
}
.form-group {
  margin-bottom: 1rem;
}
</style>
